import {useEffect, useState} from 'react';
import {
  CampaignsGetResponse,
  CampaignsGetResponse_Campaign,
  CampaignsService,
  CRegion, RegionsGetResponse, RegionsService
} from '../../../apis/api-client';
import FiestaInfiniteSpinner from 'src/shared/infinite-spinner/FiestaInfiniteSpinner';
import {DataView} from "primereact/dataview";
import CampaignCard from "./components/CampaignCard";
import {InputText} from "primereact/inputtext";
import {Dropdown} from "primereact/dropdown";
import {FloatLabel} from "primereact/floatlabel";
import {Button} from "primereact/button";
import {SelectItem} from "primereact/selectitem";
import {t} from "i18next";

export default function CampaignsComponent() {

  // Campaign objects.
  const [campaigns, setCampaigns] =
    useState<CampaignsGetResponse_Campaign[]>([]);

  const take = 12;

  const [autoUpdate, setAutoUpdate] =
    useState(false);
  const [more, setMore] =
    useState<boolean>(true);
  const [searchValue, setSearchValue] =
    useState<string | undefined>(undefined);
  const [regionValue, setRegionValue] =
    useState<CRegion>(CRegion.EUROPE);
  const [loading, setLoading] =
    useState<boolean>(false);
  const [regionOptions, setRegionOptions] =
    useState<SelectItem[]>(
      [
        { value: CRegion.EUROPE },
        { value: CRegion.NORTH_AMERICA }
      ]
    );

  /**
   * Initally load the regions.
   */
  useEffect(
    () => {
      (
        async () => {
          const response : RegionsGetResponse =
            await RegionsService
              .get(true);

          if (response) {
            if (response.availableRegions) {
              const newSelectItems: SelectItem[] =
                response
                  .availableRegions
                  .map(
                    (r) => {
                      return {
                        value: r
                      };
                    }
                  );

              setRegionOptions(newSelectItems);

              if (response.suggested)
                setRegionValue(response.suggested);
            }
          }
        }
      )();
    },
    []
  );

  /**
   * On search value change, load the campaigns.
   */
  useEffect(
    () => {
      (
        async () => {
          if (!autoUpdate)
            return;

          if (searchValue) {
            if (searchValue?.length >= 2) {
              const response =
                await loadCampaigns(
                  0,
                  take,
                  searchValue,
                  regionValue
                );

              if (response) {
                setCampaigns(response!.result!);
                setMore(response!.result!.length < response.totalCount!);
              }
            }
          } else {
            const response =
              await loadCampaigns(
                0,
                take,
                searchValue,
                regionValue
              );

            if (response) {
              setCampaigns(response!.result!);
              setMore(response!.result!.length < response.totalCount!);
            }
          }
        }
      )();
    },
    [
      searchValue,
      regionValue
    ]
  );

  /**
   * Initially check for the parameters and execute the initial search.
   */
  useEffect(
    () => {
      (
        async () => {
          let loadInitial = false;
          let search: string | undefined;
          let region: CRegion | undefined;

          const searchParams = new URLSearchParams(window.location.search);
          if (searchParams) {
            const searchTemp = searchParams.get('s');
            if (searchTemp) {
              loadInitial = true;
              setSearchValue(searchTemp);
              search = searchTemp;
            }

            const regionTemp = searchParams.get('r');
            if (regionTemp) {
              if (regionTemp as CRegion) {
                loadInitial = true;
                setRegionValue(regionTemp as CRegion);
                region = regionTemp as CRegion;
              }
            }
          }

          if (loadInitial) {
            const response =
              await loadCampaigns(
                0,
                take,
                search as string,
                region as CRegion
              );

            if (response) {
              setCampaigns(response!.result!);
              setMore(response!.result!.length < response.totalCount!);
              window.location.href = '#campaigns'
            }
          } else {
            const response =
              await loadCampaigns(
                0,
                take,
                searchValue,
                regionValue
              );

            if (response) {
              setCampaigns(response!.result!);
              setMore(response!.result!.length < response.totalCount!);
            }
          }

          setAutoUpdate(true);
        }
      )();
    },
    []
  );

  /**
   * Loads the campaigns from the API.
   */
  async function loadCampaigns(
    skip: number,
    take: number,
    searchTerm: string | undefined = undefined,
    region: CRegion | undefined = undefined,
  ) {
    if (loading)
      return;

    setLoading(true);
    try {
      let noCache: boolean = false;
      if (new URLSearchParams(window.location.search).get('noCache') === 'true')
        noCache = true;

      let response: CampaignsGetResponse
        = await CampaignsService
          .getLandingpage(
            skip,
            take,
            noCache,
            undefined,
            searchTerm,
            region
          );

      if (
        response
        && response.result
      ) {
        return response;
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  /**
   * Renders the item, which can be used in the grid view.
   * @param item
   */
  function itemTemplate(item: CampaignsGetResponse_Campaign) {
    return <>
      <div className={'col-12 md:col-6 xl:col-4 p-1 pt-4'}>
        <CampaignCard
          campaign={item}
        />
      </div>
    </>;
  }

  return <>
    <style>
      {
        `
          .p-dataview-content {
            background-color: transparent;
          }
          
          .p-dataview-content {
            width: 100%;
          }
        `
      }
    </style>
    <div
      id='campaigns'
      style={
        {
          backgroundColor: 'black',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'bottom',
        }
      }
      className='flex flex-column align-items-center justify-content-center mb-8'
    >
      {
        <div className={'grid col-12 xl:col-offset-0 xl:col-9 mt-4 p-4'} style={{maxWidth: '1480px'}}>
          <div className={'flex flex-row gap-2 w-full'}>
            <FloatLabel pt={{root: {style: {width: '100%'}}}}>
              <InputText
                id={'search'}
                placeholder={t('joinPage.search.placeholder')}
                className={'flex flex-1'}
                style={{width: '100%'}}
                value={searchValue}
                onChange={
                  (e)=> setSearchValue(e.target.value)
                }
              />
              <label htmlFor="search">{t('joinPage.search.label')}</label>
            </FloatLabel>
            <FloatLabel>
              <Dropdown
                id={'region'}
                className={'flex'}
                value={regionValue}
                valueTemplate={
                  (item) =>
                    t(`joinPage.regions.${item.value}`)
                }
                options={regionOptions}
                itemTemplate={
                  (item) =>
                    t(`joinPage.regions.${item.value}`)
                }
                onChange={(e) => setRegionValue(e.target.value)}
              />
              <label htmlFor="region">{t('joinPage.region.label')}</label>
            </FloatLabel>
          </div>
        </div>
      }
      {
        campaigns.length > 0
        &&
        <DataView
          value={campaigns}
          layout={'grid'}
          className='grid col-12 xl:col-offset-0 xl:col-9'
          data-pr-classname={'w-full'}
          gutter={false}
          itemTemplate={itemTemplate}
          style={{maxWidth: '1480px'}}
        />
      }
      {
        loading
        &&
        <FiestaInfiniteSpinner/>
      }
      {
        !loading
        && campaigns.length == 0
        &&
        <p>{t('joinPage.noResultsMessage')}</p>
      }
      {
        more
        &&
          <div className={'grid col-12 xl:col-offset-0 xl:col-9 mt-4 p-4'} style={{maxWidth: '300px'}}>
            <div className={'flex flex-column justify-content-center w-full'}>
              <Button
                outlined
                label={t('joinPage.search.loadMore')}
                onClick={
                  async () => {
                    const response =
                      await loadCampaigns(
                        campaigns.length,
                        take,
                        searchValue,
                        regionValue
                      );

                    if (response) {
                      setMore(response!.result!.length + campaigns.length < response.totalCount!);
                      response
                        .result!
                        .map(c => campaigns.push(c));
                    }
                  }
                }
              />
            </div>
          </div>
      }
    </div>
  </>;
}